// Thin wrapper over the public projection API used by the home + rankings views.
// All endpoints are read-only and cacheable.

import { apiUrl } from './apiBase';

export interface RankingItem {
  playerId: number;
  name: string;
  firstName?: string;
  lastName?: string;
  rating: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  gamesDrawn: number;
  ratingDifference: number;
  lifetimeRatingDifference?: number;
  startingRating: number;
  firstTournament: number;
  latestTournament: number;
  tournamentsPlayed: number;
}

export interface TournamentProjectionItem {
  tournamentNumber: number;
  name?: string;
  date?: string | null;
  matchCount?: number;
  singlesMatchCount?: number;
  doublesMatchCount?: number;
  uniquePlayerCount?: number;
  // When this projection row aggregates a single LeagueRound (e.g. Bieruń
  // kolejka 10 across 5 groups), `leagueId` lets the viewer link straight
  // to the league page.
  leagueRoundId?: number | null;
  leagueId?: number | null;
  leagueClubPublicListed?: boolean | null;
}

export interface HomeStats {
  singlesPlayers: number;
  doublesPlayers: number;
  singlesMatches: number;
  doublesMatches: number;
  tournaments: number;
}

export interface MatchEntry {
  // Ledger order inside a single day — the ELO chain is keyed on (occurredAt, matchId).
  matchId?: number;
  tournamentNumber: number;
  tournamentName?: string;
  tournamentFile?: string;
  // When the match was played. The tournament number no longer orders matches
  // in time — a kolejka closed in the panel is numbered -roundId.
  occurredAt?: string;
  dataType: string;
  isSingleMatch: boolean;
  player1?: string;
  player2?: string;
  player1Id?: number;
  player2Id?: number;
  team1?: string[];
  team2?: string[];
  team1Ids?: number[];
  team2Ids?: number[];
  score: number;
  setScores?: string[];
  matchResult?: string;
  fractional?: boolean;
  // Waga, którą księga zapłaciła za ten mecz (americano 0.5, PZBad 1.75, …).
  // null = mecz policzony, zanim księga zaczęła ją zapisywać.
  ratingMultiplier?: number | null;
  winner: number;
  player1RatingBefore?: number;
  player1RatingAfter?: number;
  player1RatingChange?: number;
  player2RatingBefore?: number;
  player2RatingAfter?: number;
  player2RatingChange?: number;
  team1RatingBefore?: number;
  team1RatingAfter?: number;
  team1RatingChange?: number;
  team2RatingBefore?: number;
  team2RatingAfter?: number;
  team2RatingChange?: number;
}

export interface PlayerProfileBlock {
  id: number;
  firstName: string;
  lastName: string;
}

export interface PlayerSummary {
  player?: PlayerProfileBlock;
  singles?: RankingItem;
  doubles?: RankingItem;
}

interface Paginated<T> {
  items: T[];
  total: number;
}

interface FetchPageOptions {
  limit?: number;
  offset?: number;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path));
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} on ${path}`);
  }

  return (await res.json()) as T;
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      search.set(key, String(value));
    }
  }

  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export function fetchTopSingles(limit: number): Promise<Paginated<RankingItem>> {
  return getJson(withQuery('/api/public/rankings/singles', { limit }));
}

export function fetchTopDoubles(limit: number): Promise<Paginated<RankingItem>> {
  return getJson(withQuery('/api/public/rankings/doubles', { limit }));
}

export function fetchAllSingles(opts: FetchPageOptions = {}): Promise<Paginated<RankingItem>> {
  return getJson(withQuery('/api/public/rankings/singles', { limit: opts.limit, offset: opts.offset }));
}

export function fetchAllDoubles(opts: FetchPageOptions = {}): Promise<Paginated<RankingItem>> {
  return getJson(withQuery('/api/public/rankings/doubles', { limit: opts.limit, offset: opts.offset }));
}

export function fetchRecentTournaments(limit: number): Promise<Paginated<TournamentProjectionItem>> {
  return getJson(withQuery('/api/public/tournaments', { limit }));
}

export function fetchAllTournaments(opts: FetchPageOptions = {}): Promise<Paginated<TournamentProjectionItem>> {
  return getJson(withQuery('/api/public/tournaments', { limit: opts.limit, offset: opts.offset }));
}

export function fetchHomeStats(): Promise<HomeStats> {
  return getJson('/api/public/stats');
}

export function fetchPlayerSummary(playerId: number): Promise<PlayerSummary> {
  return getJson(`/api/public/players/${playerId}`);
}

interface FetchPlayerMatchesOptions extends FetchPageOptions {
  kind?: 'singles' | 'doubles';
}

export function fetchPlayerMatches(
  playerId: number,
  opts: FetchPlayerMatchesOptions = {},
): Promise<Paginated<MatchEntry>> {
  return getJson(
    withQuery(`/api/public/players/${playerId}/matches`, {
      limit: opts.limit,
      offset: opts.offset,
      kind: opts.kind,
    }),
  );
}

export interface RatingHistoryEntry {
  playedAt: string;
  sequenceInFile: number | null;
  ratingBefore: number;
  ratingAfter: number;
  matchKind: string;
  matchId: number | null;
  tournamentNumber: number | null;
}

export interface RatingHistoryResponse {
  playerId: number;
  discipline: 'SINGLES' | 'DOUBLES';
  total: number;
  items: RatingHistoryEntry[];
}

export function fetchPlayerRatingHistory(
  playerId: number,
  discipline: 'SINGLES' | 'DOUBLES',
  limit = 5000,
): Promise<RatingHistoryResponse> {
  return getJson(
    withQuery(`/api/public/players/${playerId}/rating-history`, { discipline, limit }),
  );
}
